import { ArrowLeft } from "lucide-react";
import {
  memo,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
} from "react";

import { cn } from "../../lib/cn";

import { Button } from "../button/button";

export type PageHeaderProps = {
  breadcrumb?: ReactNode;
  title: string;
  titleAppearance?: "default" | "prominent";
  titleAs?: "h1" | "h2";
  desc?: string;
  isBeta?: boolean;
  topRightNode?: ReactNode;
  bottomNode?: ReactNode;
  useBackButton?: {
    onClick: MouseEventHandler<HTMLButtonElement>;
  };
  style?: CSSProperties;
  className?: string;
  contentClassName?: string;
};

function PageHeaderComponent({
  breadcrumb,
  title,
  titleAppearance,
  titleAs = "h2",
  desc,
  isBeta = false,
  topRightNode,
  bottomNode,
  useBackButton,
  style,
  className,
  contentClassName,
}: PageHeaderProps) {
  const Title = titleAs;
  const resolvedTitleAppearance =
    titleAppearance ?? (titleAs === "h1" ? "prominent" : "default");

  return (
    <header className={cn("w-full shrink-0", className)}>
      {breadcrumb ? <div className="px-8 pt-5">{breadcrumb}</div> : null}
      <div
        className={cn(
          "z-10 flex w-full items-center justify-between overflow-hidden px-8 pb-5 gap-3",
          breadcrumb ? "pt-2" : "pt-5",
          contentClassName,
        )}
      >
        <div className="flex min-w-0 flex-col gap-1">
          <Title
            className={cn(
              "m-0 min-w-0 leading-tight text-gray-900",
              resolvedTitleAppearance === "prominent"
                ? "text-3xl font-bold tracking-tight"
                : "text-base font-semibold",
            )}
          >
            <span className="flex h-8 min-w-0 items-center gap-2.5">
              <span className="truncate">{title}</span>
              {isBeta ? (
                <span className="inline-flex h-4 w-[38px] items-center justify-center rounded bg-gray-700 text-[10px] font-bold leading-none text-white">
                  BETA
                </span>
              ) : null}
            </span>
          </Title>

          {desc ? (
            <p className="min-h-6 truncate text-sm leading-[22px] text-gray-600">
              {desc}
            </p>
          ) : null}
        </div>

        {topRightNode}
      </div>

      {(useBackButton || bottomNode) && (
        <div className="px-8 pb-2" style={style}>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-3">
              {useBackButton ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Go back"
                  onClick={useBackButton.onClick}
                >
                  <ArrowLeft className="size-4" />
                </Button>
              ) : null}
            </div>
          </div>

          {bottomNode}
        </div>
      )}
    </header>
  );
}

export const PageHeader = memo(PageHeaderComponent);

PageHeader.displayName = "PageHeader";
